import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminSeasonsService } from '../services/admin.seasons.service'
import type { SeasonDocument } from '@/types'
import type { SeasonFormValues } from '../types'

interface UseAdminSeasonsResult {
  seasons: Array<SeasonDocument & { id: string }>
  loading: boolean
  error: string | null
  createSeason: (data: SeasonFormValues) => Promise<string>
  updateSeason: (id: string, data: Partial<SeasonFormValues>) => Promise<void>
  deleteSeason: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminSeasons(): UseAdminSeasonsResult {
  const [seasons, setSeasons] = useState<Array<SeasonDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminSeasonsService
      .getAllSeasons()
      .then(setSeasons)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load seasons'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createSeason = useCallback(async (data: SeasonFormValues): Promise<string> => {
    const id = await adminSeasonsService.createSeason(data)
    refresh()
    return id
  }, [refresh])

  const updateSeason = useCallback(
    async (id: string, data: Partial<SeasonFormValues>): Promise<void> => {
      await adminSeasonsService.updateSeason(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteSeason = useCallback(async (id: string): Promise<void> => {
    await adminSeasonsService.deleteSeason(id)
    refresh()
  }, [refresh])

  return { seasons, loading, error, createSeason, updateSeason, deleteSeason, refresh }
}
